import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import AIChatIcon from "@atlaskit/icon/core/ai-chat";
import BugIcon from "@atlaskit/icon/core/bug";
import VideoIcon from "@atlaskit/icon/core/video";
import NotepadIcon from "@atlaskit/icon-lab/core/notepad";

import { useUserContext } from "@/components/user-context/user-context-provider";
import { Link } from "@/libraries/design/components";
import { ContentTypeImage } from "@/templates/home/home-page/src/sections-v2/jsm-xflow-hero/content-type-image.tsx";
import type {
    TabProps,
    TabType
} from "@/templates/home/home-page/src/sections-v2/jsm-xflow-hero/tab-type.tsx";
import { tabbedHeroSectionLoomAIMessages } from "@/templates/home/home-page/src/sections-v2/reusable-xflow-hero-component/tabbed-hero-section.messages.tsx";

type LoomAIContentTypeProps = TabProps;

export const LoomAIContentType: TabType = ({}: LoomAIContentTypeProps) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { user } = useUserContext();

    return (
        <ContentTypeImage
            imageSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/y0ysg6vype03e3v0y686iyq6j207nv8b.webp"
            imageAlt="This is an screenshot of a Loom video with an arrow pointing to a document of an AI generated summary."
            title={intl.formatMessage(
                tabbedHeroSectionLoomAIMessages.contentTitle
            )}
            description={intl.formatMessage(
                tabbedHeroSectionLoomAIMessages.contentDescription
            )}
            hideSeparator={true}
            iconCollection={[
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionLoomAIMessages.contentFeaturePoint1
                    ),
                    icon: <NotepadIcon label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionLoomAIMessages.contentFeaturePoint2
                    ),
                    icon: <BugIcon label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionLoomAIMessages.contentFeaturePoint3
                    ),
                    icon: <AIChatIcon label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionLoomAIMessages.contentFeaturePoint4
                    ),
                    icon: <VideoIcon label="" />
                }
            ]}
            cta={
                <Link
                    href={"/software/loom"}
                    text={intl.formatMessage(
                        tabbedHeroSectionLoomAIMessages.contentCta
                    )}
                    type={"primary"}
                    dataEventProps={{
                        "data-product-key": "loom",
                        "data-event-component": "linkButton",
                        "data-event-container": "tabsMenu",
                        "data-event": "clicked",
                        "data-label": "try-it-out",
                        "data-label-english": "Explore Loom",
                        "data-track-event": "loom-pt.content-types.video.cta"
                    }}
                    onClick={() => {
                        createAnalyticsEvent({
                            eventName: "loom-crossflow-tab-cta-clicked",
                            eventType: "ui",
                            action: "clicked",
                            actionSubject: "linkButton",
                            actionSubjectId: "loomCrossFlowHeroSection",
                            attributes: {
                                reactComponent: "LoomAIContentType",
                                tab: "loom-ai",
                                userTeamType: user.extended_profile?.team_type
                            }
                        }).fire();
                    }}
                />
            }
            reverseLayout
        />
    );
};
